import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicBreakdown } from '@/lib/api';
import {
  ShoppingBag, Home, DollarSign, ExternalLink,
  X, ChevronLeft, ChevronRight, ChevronDown, ImageOff, Calendar, FileText,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (path) => path ? `${API}/api/furniture-projects/files/${path}` : '';

const fmt = (n) => {
  const v = Math.round(Number(n || 0));
  return `$${v.toLocaleString('en-US')}`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return ''; }
};

// Palette matches the reference mock: warm ivory background, soft
// sage-green cards, dark forest ink, muted brass accents for "remaining"
// counts.
const PALETTE = {
  bg: '#f4efe5',
  card: '#fbf8f0',
  ink: '#1c3a2e',
  ink2: '#2d4a3d',
  mute: '#6b7969',
  brass: '#a37f2c',
  hairline: '#e7dfd0',
  chip: '#eee6d3',
};

/**
 * PublicBreakdownPage
 *
 * Branded, mobile-friendly cost breakdown for a furniture project. Reached
 * via /p/:token on the share domain (or /breakdown/:token as a legacy
 * fallback on any host).
 *
 * Layout mirrors the design mock:
 *   - Sticky top bar with FL brand + section anchors + PDF button
 *   - Big serif title, address, "last updated"
 *   - Right-aligned Project Investment card
 *   - 4 stat tiles (Budget / Purchased / Remaining / Rooms)
 *   - Room summary: expandable cards with mood-board thumbnails
 *   - Purchases anchor: receipts grouped by vendor
 *   - Bottom trio of info cards + footer
 */
export default function PublicBreakdownPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openRoom, setOpenRoom] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { receipts, index }
  // Separate lightbox for room mood-board thumbnails so users can view the
  // photo full-size without opening any receipt.
  const [photoLightbox, setPhotoLightbox] = useState(null); // { url, name }
  const [activeSection, setActiveSection] = useState('overview');

  const overviewRef = useRef(null);
  const roomsRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getPublicBreakdown(token);
        if (!cancelled) {
          setData(d);
          // All rooms start collapsed — user opens what they want to inspect.
          setOpenRoom(null);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? 'Breakdown link not found.' : 'Unable to load this breakdown.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Scroll-spy nav highlighting.
  useEffect(() => {
    const onScroll = () => {
      const sections = [
        ['overview', overviewRef.current],
        ['rooms', roomsRef.current],
      ];
      const y = window.scrollY + 140;
      let current = 'overview';
      for (const [key, el] of sections) {
        if (el && el.offsetTop <= y) current = key;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [data]);

  const scrollTo = (ref) => {
    if (!ref?.current) return;
    window.scrollTo({ top: ref.current.offsetTop - 80, behavior: 'smooth' });
  };

  const receiptsByItem = useMemo(() => {
    const map = new Map();
    (data?.receipts || []).forEach(r => {
      (r.item_ids || []).forEach(iid => {
        if (!map.has(iid)) map.set(iid, []);
        map.get(iid).push(r);
      });
    });
    return map;
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: PALETTE.bg }}>
        <div className="text-sm" style={{ color: PALETTE.mute }} data-testid="breakdown-loading">Loading breakdown…</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: PALETTE.bg }}>
        <div className="max-w-md text-center">
          <h1 className="font-display-serif text-3xl mb-2" style={{ color: PALETTE.ink }}>Not available</h1>
          <p className="text-sm" style={{ color: PALETTE.mute }} data-testid="breakdown-error">{error || 'This link is no longer available.'}</p>
        </div>
      </div>
    );
  }

  const openReceipts = (receipts, startIndex = 0) => {
    if (!receipts || receipts.length === 0) return;
    setLightbox({ receipts, index: startIndex });
  };
  const closeReceipts = () => setLightbox(null);
  const nextReceipt = () => setLightbox(l => l ? { ...l, index: (l.index + 1) % l.receipts.length } : l);
  const prevReceipt = () => setLightbox(l => l ? { ...l, index: (l.index - 1 + l.receipts.length) % l.receipts.length } : l);

  return (
    <div className="min-h-screen" style={{ background: PALETTE.bg, color: PALETTE.ink }} data-testid="breakdown-page">
      {/* ── Sticky nav ────────────────────────────────────── */}
      <nav className="sticky top-0 z-40 border-b" style={{ background: PALETTE.card, borderColor: PALETTE.hairline }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className="h-9 w-9 rounded-full border-[1.5px] flex items-center justify-center text-[11px] font-heading font-bold tracking-tight flex-shrink-0"
              style={{ borderColor: PALETTE.ink, color: PALETTE.ink }}
            >FL</div>
            <div className="leading-none min-w-0">
              <div className="font-heading text-[13px] tracking-[0.14em] font-semibold" style={{ color: PALETTE.ink }}>FLEX LIVING</div>
              <div className="font-heading text-[10px] tracking-[0.22em]" style={{ color: PALETTE.mute }}>MIAMI</div>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-8">
            {[
              ['overview', 'Overview', overviewRef],
              ['rooms', 'Rooms', roomsRef],
            ].map(([key, label, ref]) => (
              <button
                key={key}
                onClick={() => scrollTo(ref)}
                className="text-sm relative py-4 transition-colors"
                style={{
                  color: activeSection === key ? PALETTE.ink : PALETTE.mute,
                  fontWeight: activeSection === key ? 600 : 500,
                }}
                data-testid={`nav-${key}`}
              >
                {label}
                {activeSection === key && (
                  <span className="absolute left-0 right-0 -bottom-[1px] h-[2px]" style={{ background: PALETTE.ink }} />
                )}
              </button>
            ))}
          </div>

          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm rounded-md px-3 py-1.5 border transition-colors hover:bg-white"
            style={{ borderColor: PALETTE.hairline, color: PALETTE.ink }}
            data-testid="download-pdf-btn"
          >
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Download PDF</span>
            <span className="sm:hidden">PDF</span>
          </button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* ── Overview header ───────────────────────────── */}
        <section ref={overviewRef} data-testid="overview-section">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-10 items-start">
            <div>
              <h1 className="font-display-serif text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight leading-none" data-testid="breakdown-title" style={{ color: PALETTE.ink }}>
                {data.title || 'Furniture Project'}
              </h1>
              {data.subtitle && (
                <p className="mt-3 text-base sm:text-lg leading-snug whitespace-pre-line" style={{ color: PALETTE.ink2 }} data-testid="breakdown-subtitle">
                  {data.subtitle}
                </p>
              )}
              {data.last_updated && (
                <div className="mt-5 inline-flex items-center gap-1.5 text-[13px]" style={{ color: PALETTE.mute }}>
                  <Calendar className="h-3.5 w-3.5" />
                  Last updated {fmtDate(data.last_updated)}
                </div>
              )}
              {data.message && (
                <div className="mt-4 text-sm whitespace-pre-wrap max-w-xl italic" style={{ color: PALETTE.ink2 }} data-testid="breakdown-message">
                  {data.message}
                </div>
              )}
            </div>

            {/* Project Cost card */}
            <div className="rounded-2xl px-6 py-6 sm:py-7" style={{ background: PALETTE.card, border: `1px solid ${PALETTE.hairline}` }} data-testid="investment-card">
              <p className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: PALETTE.mute }}>Project Cost</p>
              <p className="font-display-serif text-5xl font-normal mt-1 tabular-nums" style={{ color: PALETTE.ink }} data-testid="breakdown-grand-total">{fmt(data.grand_total)}</p>
              <div className="border-t mt-4 pt-4" style={{ borderColor: PALETTE.hairline }}>
                <StatMini icon={ShoppingBag} value={data.items_purchased} label="Items Purchased" />
              </div>
            </div>
          </div>

          {/* ── Stat tiles ────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-8">
            <StatCard icon={DollarSign} label="Total Budget" value={fmt(data.total_budget || data.grand_total)} />
            <StatCard icon={ShoppingBag} label="Purchased" value={data.items_purchased} suffix="Items" />
            <StatCard icon={Home} label="Rooms" value={data.room_count} suffix={data.room_count === 1 ? 'Room' : 'Rooms'} />
          </div>
        </section>

        {/* ── Room summary ──────────────────────────────── */}
        <section ref={roomsRef} data-testid="rooms-section" className="mt-14">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="font-display-serif text-3xl sm:text-4xl font-normal tracking-tight" style={{ color: PALETTE.ink }}>Room Summary</h2>
              <p className="text-sm mt-1" style={{ color: PALETTE.mute }}>Click a room to view items</p>
            </div>
            <div className="text-sm" style={{ color: PALETTE.mute }}>
              {data.rooms.length} of {data.rooms.length} rooms
            </div>
          </div>

          <div className="space-y-3">
            {data.rooms.length === 0 && (
              <p className="text-sm py-6 text-center" style={{ color: PALETTE.mute }}>No rooms included in this breakdown.</p>
            )}
            {data.rooms.map(r => (
              <RoomCard
                key={r.id}
                room={r}
                expanded={openRoom === r.id}
                onToggle={() => setOpenRoom(openRoom === r.id ? null : r.id)}
                onOpenPhoto={(url, name) => setPhotoLightbox({ url, name })}
                receiptsByItem={receiptsByItem}
                onOpenReceipts={openReceipts}
                showBudget={!!data.display_options?.show_budget}
                showSpecs={!!data.display_options?.show_specifications}
                showNotes={!!data.display_options?.show_notes}
                showStatus={!!data.display_options?.show_status}
                showLinks={!!data.display_options?.show_links}
              />
            ))}
          </div>
        </section>

        {/* ── Purchases section removed per user request. ─── */}

        <footer className="mt-16 pt-6 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs" style={{ borderColor: PALETTE.hairline, color: PALETTE.mute }}>
          <span>&copy; {new Date().getFullYear()} Flex Living Miami</span>
          <span className="italic">Designed with care. Made for comfortable living.</span>
        </footer>
      </main>

      {/* ── Receipt lightbox ───────────────────────────── */}
      {lightbox && <ReceiptLightbox lightbox={lightbox} onClose={closeReceipts} onPrev={prevReceipt} onNext={nextReceipt} />}

      {/* ── Room photo lightbox ───────────────────────── */}
      {photoLightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setPhotoLightbox(null)}
          data-testid="photo-lightbox"
        >
          <button
            onClick={() => setPhotoLightbox(null)}
            className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full p-2 shadow"
            title="Close"
            data-testid="close-photo-lightbox"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img
              src={photoLightbox.url}
              alt={photoLightbox.name}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            {photoLightbox.name && (
              <div className="text-white text-sm font-medium tracking-wide">{photoLightbox.name}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sub-components ───────────────────────────────── */

const StatMini = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-2.5">
    <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: '#e7dfd0' }}>
      <Icon className="h-4 w-4" style={{ color: PALETTE.ink }} />
    </div>
    <div className="min-w-0">
      <div className="font-heading text-lg font-semibold leading-none tabular-nums" style={{ color: PALETTE.ink }}>{value}</div>
      <div className="text-[11px] leading-tight mt-0.5" style={{ color: PALETTE.mute }}>{label}</div>
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, suffix }) => (
  <div className="rounded-xl p-4 sm:p-5" style={{ background: PALETTE.card, border: `1px solid ${PALETTE.hairline}` }}>
    <div className="h-9 w-9 rounded-full flex items-center justify-center mb-3" style={{ background: '#e7dfd0' }}>
      <Icon className="h-4 w-4" style={{ color: PALETTE.ink }} />
    </div>
    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: PALETTE.mute }}>{label}</div>
    <div className="mt-1 flex items-baseline gap-1.5">
      <span className="font-heading text-2xl sm:text-3xl font-semibold tabular-nums" style={{ color: PALETTE.ink }}>{value}</span>
      {suffix && <span className="text-sm" style={{ color: PALETTE.mute }}>{suffix}</span>}
    </div>
  </div>
);

const RoomCard = ({ room, expanded, onToggle, onOpenPhoto, receiptsByItem, onOpenReceipts, showBudget, showSpecs, showNotes, showStatus, showLinks }) => {
  const hasNoWork = room.purchased_count === 0 && room.remaining_count === 0;
  const notStarted = room.purchased_count === 0;

  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{ background: PALETTE.card, border: `1px solid ${PALETTE.hairline}` }}
      data-testid={`room-card-${room.id}`}
    >
      <div className="flex items-stretch">
        {/* Thumbnail — its own click target opens the photo lightbox. */}
        <div className="p-3 sm:p-4 pr-0 sm:pr-0 flex-shrink-0">
          {room.thumbnail ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenPhoto(fileUrl(room.thumbnail), room.name); }}
              className="h-14 w-20 sm:h-16 sm:w-24 rounded-lg overflow-hidden block group relative"
              style={{ background: PALETTE.chip }}
              title="View room photo"
              data-testid={`room-photo-${room.id}`}
            >
              <img
                src={fileUrl(room.thumbnail)}
                alt={room.name}
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </button>
          ) : (
            <div className="h-14 w-20 sm:h-16 sm:w-24 rounded-lg overflow-hidden flex items-center justify-center" style={{ background: PALETTE.chip }}>
              <ImageOff className="h-5 w-5" style={{ color: PALETTE.mute }} />
            </div>
          )}
        </div>

        {/* Toggle target — everything else on the header row opens/closes the items list. */}
        <button
          onClick={onToggle}
          className="flex-1 min-w-0 flex items-center gap-3 sm:gap-4 p-3 sm:p-4 pl-3 sm:pl-4 text-left hover:bg-white/40 transition-colors"
          data-testid={`room-toggle-${room.id}`}
        >
        <div className="min-w-0 flex-1 grid grid-cols-2 sm:grid-cols-[1.5fr_1fr_1fr_auto] items-center gap-2 sm:gap-3">
          <div className="min-w-0">
            <div className="font-heading text-base sm:text-lg font-semibold truncate" style={{ color: PALETTE.ink }}>{room.name}</div>
            <div className="text-xs mt-0.5 tabular-nums" style={{ color: PALETTE.mute }}>
              {room.room_total > 0 ? <><span className="font-medium" style={{ color: PALETTE.ink2 }}>{fmt(room.room_total)}</span> · Room Total</> : 'Pending · Room Total'}
            </div>
          </div>
          <div className="hidden sm:block text-center">
            <div className="font-heading text-lg font-semibold tabular-nums" style={{ color: PALETTE.ink }}>{room.purchased_count}</div>
            <div className="text-[11px]" style={{ color: PALETTE.mute }}>Purchased</div>
          </div>
          <div className="hidden sm:block text-center">
            <div className="font-heading text-lg font-semibold tabular-nums" style={{ color: PALETTE.ink }}>{hasNoWork ? '—' : `${room.percent_complete}%`}</div>
            <div className="text-[11px]" style={{ color: PALETTE.mute }}>{notStarted ? 'Not started' : 'Complete'}</div>
          </div>
          <div className="col-span-2 sm:col-span-1 flex justify-end">
            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} style={{ color: PALETTE.mute }} />
          </div>
        </div>
      </button>
      </div>

      {expanded && (
        <div className="border-t" style={{ borderColor: PALETTE.hairline }}>
          {room.items.length > 0 && (
            <div className="px-3 sm:px-5 py-4">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-2.5" style={{ color: PALETTE.mute }}>Purchased Items</div>
              <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5 sm:gap-2">
                {room.items.map(it => {
                  const attached = receiptsByItem.get(it.id) || [];
                  return (
                    <li
                      key={it.id}
                      className="rounded-md px-2.5 py-2 flex items-start gap-2"
                      style={{ border: `1px solid ${PALETTE.hairline}`, background: 'rgba(255,255,255,0.35)' }}
                      data-testid={`item-${it.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[13px] font-medium leading-tight" style={{ color: PALETTE.ink }}>{it.name}</span>
                          {it.quantity > 1 && (
                            <span className="text-[10px] px-1 py-0.5 rounded-full font-medium" style={{ background: PALETTE.chip, color: PALETTE.ink }}>× {it.quantity}</span>
                          )}
                          {showStatus && it.status && (
                            <span className="text-[9px] uppercase tracking-wide px-1 py-0.5 rounded" style={{ background: PALETTE.chip, color: PALETTE.mute }}>{it.status}</span>
                          )}
                        </div>
                        <div className="mt-0.5 flex items-baseline gap-1.5 flex-wrap">
                          <span className="text-[13px] font-medium tabular-nums" style={{ color: PALETTE.ink }}>{fmt(it.ordered_price)}</span>
                          {showBudget && it.budget != null && Number(it.budget) > 0 && (
                            <span className="text-[9px] tabular-nums" style={{ color: PALETTE.mute }}>Budget {fmt(it.budget)}</span>
                          )}
                        </div>
                        {showSpecs && it.specifications && (
                          <p className="text-[10px] mt-1 whitespace-pre-wrap leading-snug" style={{ color: PALETTE.mute }}>{it.specifications}</p>
                        )}
                        {showNotes && it.notes && (
                          <p className="text-[10px] mt-0.5 italic whitespace-pre-wrap leading-snug" style={{ color: PALETTE.mute }}>{it.notes}</p>
                        )}
                        {showLinks && Array.isArray(it.links) && it.links.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {it.links.map((lnk, i) => {
                              const url = typeof lnk === 'string' ? lnk : lnk?.url;
                              if (!url) return null;
                              let host = 'Link';
                              try { host = new URL(url).hostname; } catch { /* keep default */ }
                              return (
                                <a key={i} href={url} target="_blank" rel="noreferrer" className="text-[10px] inline-flex items-center gap-0.5 underline underline-offset-2" style={{ color: PALETTE.ink2 }}>
                                  <ExternalLink className="h-2.5 w-2.5" />{typeof lnk === 'object' && lnk?.label ? lnk.label : host}
                                </a>
                              );
                            })}
                          </div>
                        )}
                        {attached.length > 0 && (
                          <button
                            onClick={() => onOpenReceipts(attached)}
                            className="mt-1 text-[10px] font-medium inline-flex items-center gap-0.5 underline underline-offset-2"
                            style={{ color: PALETTE.ink2 }}
                            data-testid={`view-receipt-${it.id}`}
                          >
                            View Receipt{attached.length > 1 ? 's' : ''}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {room.pending_items.length > 0 && (
            <div className="px-3 sm:px-5 pb-4">
              <div className="rounded-lg px-4 py-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" style={{ background: '#f4ecd3', border: `1px solid #e7d9b0` }}>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: PALETTE.brass }}>Items Still Being Sourced</div>
                  <div className="text-sm mt-1" style={{ color: PALETTE.ink2 }}>
                    {room.pending_items.slice(0, 5).map(i => i.name).join(', ')}
                    {room.pending_items.length > 5 && ` + ${room.pending_items.length - 5} more`}
                  </div>
                </div>
              </div>
            </div>
          )}

          {room.items.length === 0 && room.pending_items.length === 0 && (
            <p className="px-5 py-6 text-sm text-center" style={{ color: PALETTE.mute }}>No items in this room.</p>
          )}
        </div>
      )}
    </div>
  );
};

const ReceiptLightbox = ({ lightbox, onClose, onPrev, onNext }) => {
  const r = lightbox.receipts[lightbox.index];
  const url = fileUrl(r?.file_path);
  const type = r?.content_type || '';
  const isImage = type.startsWith('image/');
  const isPdf = type === 'application/pdf' || (r?.file_name || '').toLowerCase().endsWith('.pdf');
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-4xl w-full max-h-[90vh] bg-white rounded-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2 border-b" style={{ background: PALETTE.card, borderColor: PALETTE.hairline }}>
          <div className="text-sm min-w-0 flex-1 truncate" style={{ color: PALETTE.ink }}>
            <span className="font-medium">{r?.vendor || r?.file_name || 'Receipt'}</span>
            {r?.amount != null && (
              <span className="ml-2" style={{ color: PALETTE.mute }}>{fmt(r.amount)}</span>
            )}
            {lightbox.receipts.length > 1 && (
              <span className="ml-2 text-xs" style={{ color: PALETTE.mute }}>({lightbox.index + 1} / {lightbox.receipts.length})</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <a href={url} target="_blank" rel="noreferrer" className="p-1.5" style={{ color: PALETTE.mute }} title="Open in new tab">
              <ExternalLink className="h-4 w-4" />
            </a>
            <button onClick={onClose} className="p-1.5" style={{ color: PALETTE.mute }} title="Close" data-testid="close-receipt-lightbox">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="bg-[#0f1114] flex items-center justify-center relative" style={{ minHeight: 320, maxHeight: 'calc(90vh - 44px)' }}>
          {lightbox.receipts.length > 1 && (
            <>
              <button onClick={onPrev} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-1.5 shadow" title="Previous">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button onClick={onNext} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-1.5 shadow" title="Next">
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
          {isImage
            ? <img src={url} alt={r.file_name || 'Receipt'} className="max-h-[calc(90vh-44px)] max-w-full object-contain" />
            : isPdf
              ? <iframe src={url} title="Receipt" className="w-full" style={{ height: 'calc(90vh - 44px)' }} />
              : (
                <div className="text-center text-white p-8">
                  <p className="mb-3">Preview unavailable for this file type.</p>
                  <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-white/10 hover:bg-white/20">
                    <ExternalLink className="h-4 w-4" /> Open file
                  </a>
                </div>
              )}
        </div>
      </div>
    </div>
  );
};
